import Layout1 from '../img/Layout/layout-1.svg'
import Layout2 from '../img/Layout/layout-2.svg'
import Layout3 from '../img/Layout/layout-3.svg'
import Layout4 from '../img/Layout/layout-4.svg'
import Layout5 from '../img/Layout/layout-5.svg'
import Layout6 from '../img/Layout/layout-6.svg'
import Layout7 from '../img/Layout/layout-7.svg'
import Layout8 from '../img/Layout/layout-8.svg'
import Layout9 from '../img/Layout/layout-9.png'

// image previews of layouts
import Layout1ImagePreview from '../img/Layout/LayoutPreview/layout1-img.svg'
import Layout2ImagePreview from '../img/Layout/LayoutPreview/layout2-img.svg'
import Layout3ImagePreview from '../img/Layout/LayoutPreview/layout3-img.svg'
import Layout4ImagePreview from '../img/Layout/LayoutPreview/layout4-img.svg'
import Layout5ImagePreview from '../img/Layout/LayoutPreview/layout5-img.svg'
import Layout6ImagePreview from '../img/Layout/LayoutPreview/layout6-img.svg'
import Layout7ImagePreview from '../img/Layout/LayoutPreview/layout7-img.svg'
import Layout8ImagePreview from '../img/Layout/LayoutPreview/layout8-img.svg'
import Layout9ImagePreview from '../img/Layout/LayoutPreview/layout9-img.png'

// video preview of layouts
import Layout1VideoPreview from '../img/Layout/LayoutPreview/layout1-vid.svg'
import Layout2VideoPreview from '../img/Layout/LayoutPreview/layout2-vid.svg'
import Layout3VideoPreview from '../img/Layout/LayoutPreview/layout3-vid.svg'
import Layout4VideoPreview from '../img/Layout/LayoutPreview/layout4-vid.svg'
import Layout5VideoPreview from '../img/Layout/LayoutPreview/layout5-vid.svg'
import Layout6VideoPreview from '../img/Layout/LayoutPreview/layout6-vid.svg'
import Layout7VideoPreview from '../img/Layout/LayoutPreview/layout7-vid.svg'
import Layout8VideoPreview from '../img/Layout/LayoutPreview/layout8-vid.svg'

import nanoid from 'nanoid'
import _ from 'lodash'

// -- Dimensions & Spacing --
const CONTAINER_WIDTH = 1023
const CONTAINER_HEIGHT = 552
const PADDING = 40 // top & bottom padding
const GUTTER = 20 // minimum spacing

// Common widths
const fullWidth = CONTAINER_WIDTH - 2 * PADDING // e.g., 903px
const halfWidth = (fullWidth - GUTTER) / 2 // e.g., ~431.5px

// Heading size calculation constants
const BASE_HEADING_HEIGHT = 66 // Minimum heading height
const FONT_SIZE = 36 // Font size in pixels
const FULL_WIDTH_CHARS_PER_LINE = 40 // Characters per line for full width
const HALF_WIDTH_CHARS_PER_LINE = 20 // Characters per line for half width
const LINE_HEIGHT = 1.3 // Line height multiplier
const EXTRA_PADDING = 20 // Extra padding for the heading box

// -- Helper Functions --
const createElement = (type, display, params) => ({
  uid: nanoid(8),
  type,
  display: display || 'CONTENT',
  params
})

// Calculate heading height based on content and width
const calculateHeadingHeight = (content, isHalfWidth = false) => {
  if (!content) return BASE_HEADING_HEIGHT

  // Strip HTML tags to get just the text content
  const stripHtml = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ''
  }

  const textContent = stripHtml(content)
  const textLength = textContent.trim().length

  // Use different character counts per line based on width
  const charsPerLine = isHalfWidth
    ? HALF_WIDTH_CHARS_PER_LINE
    : FULL_WIDTH_CHARS_PER_LINE

  // Maximum allowed lines based on column width
  const maxLines = isHalfWidth ? 3 : 2

  // Calculate the maximum number of characters that can fit in the allowed lines
  const maxChars = maxLines * charsPerLine

  // Estimate the number of lines needed (capped at the maximum)
  const estimatedLines = Math.min(
    maxLines,
    Math.max(1, Math.ceil(textLength / charsPerLine))
  )

  // Calculate the needed height based on font size, line height, and add padding
  const calculatedHeight = Math.max(
    BASE_HEADING_HEIGHT,
    Math.ceil(estimatedLines * FONT_SIZE * LINE_HEIGHT) + EXTRA_PADDING
  )

  return calculatedHeight
}

// The original base heading (holds your heading content) - now with dynamic height
const baseHeading = (content, isCenter, isHalfWidth = false) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, 'text/html')

  // First replace any h1/h2 elements with their content wrapped in strong
  doc.querySelectorAll('h1, h2').forEach(heading => {
    const p = doc.createElement('p')
    p.innerHTML = heading.innerHTML
    heading.parentNode.replaceChild(p, heading)
  })

  // Then process all p elements (including converted headings)
  doc.querySelectorAll('p').forEach(p => {
    // Apply text alignment
    p.style.textAlign = isCenter ? 'center' : 'left'

    // Check if the paragraph contains direct text nodes and no strong elements
    let hasStrongElement = p.querySelector('strong') !== null
    let hasDirectTextContent = false

    // Check for direct text nodes (not inside other elements)
    for (let i = 0; i < p.childNodes.length; i++) {
      if (
        p.childNodes[i].nodeType === Node.TEXT_NODE &&
        p.childNodes[i].textContent.trim() !== ''
      ) {
        hasDirectTextContent = true
        break
      }
    }

    // If there's direct text content and no strong elements, wrap all content in strong
    if (hasDirectTextContent && !hasStrongElement) {
      // Store the original HTML
      const originalHTML = p.innerHTML
      // Replace with strong-wrapped content
      p.innerHTML = `<strong style="font-size: 36px;">${originalHTML}</strong>`
    }
  })

  // Serialize back to HTML
  const cleanedContent = doc.body.innerHTML

  // Calculate dynamic height based on content and column width
  const headingHeight = calculateHeadingHeight(content, isHalfWidth)

  return createElement('TEXT', 'HEADING', {
    content: cleanedContent,
    x: PADDING,
    y: PADDING,
    width: fullWidth,
    height: headingHeight,
    media_format: 'TEXT'
  })
}

const textElement = (content, x, y, width, height, isCenter) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(content, 'text/html')

  // Replace all heading elements (h1-h3) with p tags first
  doc.querySelectorAll('h1, h2, h3').forEach(heading => {
    const p = doc.createElement('p')
    p.innerHTML = heading.innerHTML
    heading.parentNode.replaceChild(p, heading)
  })

  // Apply text alignment to the new <p> tags
  doc.querySelectorAll('p').forEach(p => {
    p.style.textAlign = isCenter ? 'center' : 'left'
    p.style.lineHeight = LINE_HEIGHT
    p.style.fontSize = isCenter ? `18px` : `16px`

    const allowedTags = [
      'span',
      'strong',
      'em',
      'b',
      'i',
      'u',
      'mark',
      'sup',
      'sub',
      'code',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6'
    ] // Add more if needed

    // Select the first child inside <p> (assuming it's the parent container you want to replace)
    const parentContainer = p.firstElementChild

    // Replace the parent container with <strong>
    if (parentContainer) {
      const strongElement = document.createElement('strong')
      strongElement.innerHTML = parentContainer.innerHTML
      parentContainer.replaceWith(strongElement)
    }

    // Now select and update font size for all allowed children
    const children = p.querySelectorAll(allowedTags.join(', '))
    children.forEach(child => {
      child.style.fontSize = isCenter ? '18px' : '16px'
      child.style.textAlign = isCenter ? 'center' : 'left'
    })

    p.classList.add('ql-font-mulish')
  })

  // Serialize back to HTML
  const cleanedContent = doc.body.innerHTML

  return createElement('TEXT', 'CONTENT', {
    content: cleanedContent,
    x,
    y,
    width,
    height,
    media_format: 'TEXT'
  })
}

// Helper to create a logo image element
const logoImageElement = logoData => createElement('IMAGE', 'LOGO', logoData)

/**
 * Helper for media elements.
 * - If mediaData is empty, returns an element with null content.
 * - If mediaData is an array, selects the item at the given index.
 * - Otherwise, returns a single media element.
 */
const mediaElement = (mediaData, x, y, width, height, isVideo, index = 0) => {
  if (_.isEmpty(mediaData)) {
    return createElement(isVideo ? 'VIDEO' : 'IMAGE', null, {
      content: null,
      x,
      y,
      width,
      height,
      media_format: isVideo ? 'VIDEO' : 'IMAGE'
    })
  }
  if (Array.isArray(mediaData) && isVideo) {
    const item = mediaData[index] || null
    return createElement('VIDEO', null, {
      content: item ? item.content : null,
      content_id: item ? item.content_id : null,
      x,
      y,
      width,
      height
    })
  }
  if (Array.isArray(mediaData) && !isVideo) {
    const item = mediaData[index] || null
    return createElement('IMAGE', null, {
      content: item ? item.content : null,
      content_id: item ? item.content_id : null,
      x,
      y,
      width,
      height,
      media_format: 'IMAGE'
    })
  }
  return createElement(isVideo ? 'VIDEO' : 'IMAGE', null, {
    content: mediaData,
    x,
    y,
    width,
    height,
    media_format: isVideo ? undefined : 'IMAGE',
    ...(isVideo && { content_id: 69142 })
  })
}

// Process media array data (works for both images and videos)
const processMediaArray = mediaData => {
  if (Array.isArray(mediaData) && mediaData.length > 0) {
    return mediaData.map(item => ({
      content: item.params ? item.params.content : item.content,
      content_id: item.params ? item.params.content_id : item.content_id
    }))
  }
  return mediaData
}

// -- Layout Data Generator --
const createLayoutData = (
  textData = '',
  mediaData = [],
  logoData = '',
  headingContent = '',
  isVideo = false
) => {
  const processedMediaData = processMediaArray(mediaData)

  // Create headings with appropriate width settings
  const centeredHeading = baseHeading(headingContent, true, false) // Full width
  const leftAlignedHeading = baseHeading(headingContent, false, true) // Half width

  // Calculate the dynamic heading heights
  const fullWidthHeadingHeight = centeredHeading.params.height
  const halfWidthHeadingHeight = calculateHeadingHeight(headingContent, true)

  // Recalculate the layout dimensions based on dynamic heading height
  // For layouts 1 & 2:
  const firstElementY = PADDING + fullWidthHeadingHeight + GUTTER
  const availableHeight =
    CONTAINER_HEIGHT - 2 * PADDING - fullWidthHeadingHeight
  const twoRowHeight = (availableHeight - GUTTER) / 2

  // For the text column in layouts 5–8:
  const columnHeadingY = PADDING
  const columnHeadingHeight = halfWidthHeadingHeight
  const columnTextY = columnHeadingY + columnHeadingHeight + GUTTER
  const containerContentHeight = CONTAINER_HEIGHT - 2 * PADDING
  const columnTextHeight =
    containerContentHeight - (columnHeadingHeight + GUTTER)

  // For media in layouts 5–8:
  const mediaSingleHeight = containerContentHeight
  const mediaDoubleHeight = (containerContentHeight - GUTTER) / 2

  return [
    // Layout 1 – With dynamic heading height
    {
      img: Layout1,
      layoutStyle: 'layout-1',
      previewImages: [
        { img: Layout1ImagePreview, type: 'IMAGE' },
        { img: Layout1VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => ({
        data: [
          centeredHeading,
          ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
          textElement(
            textData,
            PADDING,
            firstElementY,
            fullWidth,
            twoRowHeight,
            true
          ),
          mediaElement(
            processedMediaData,
            PADDING,
            firstElementY + twoRowHeight + GUTTER,
            fullWidth,
            twoRowHeight,
            isVideo,
            0
          )
        ]
      })
    },
    // Layout 2 – With dynamic heading height
    {
      img: Layout2,
      layoutStyle: 'layout-2',
      previewImages: [
        { img: Layout2ImagePreview, type: 'IMAGE' },
        { img: Layout2VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => ({
        data: [
          centeredHeading,
          ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
          textElement(
            textData,
            PADDING,
            firstElementY,
            fullWidth,
            twoRowHeight,
            true
          ),
          mediaElement(
            processedMediaData,
            PADDING,
            firstElementY + twoRowHeight + GUTTER,
            halfWidth,
            twoRowHeight,
            isVideo,
            0
          ),
          mediaElement(
            processedMediaData,
            PADDING + halfWidth + GUTTER,
            firstElementY + twoRowHeight + GUTTER,
            halfWidth,
            twoRowHeight,
            isVideo,
            1
          )
        ]
      })
    },
    // Layout 3 – With dynamic heading height
    {
      img: Layout3,
      layoutStyle: 'layout-3',
      previewImages: [
        { img: Layout3ImagePreview, type: 'IMAGE' },
        { img: Layout3VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => ({
        data: [
          ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
          mediaElement(
            processedMediaData,
            PADDING,
            PADDING,
            fullWidth,
            twoRowHeight,
            isVideo,
            0
          ),
          {
            ...centeredHeading,
            params: {
              ...centeredHeading.params,
              x: PADDING,
              y: PADDING + twoRowHeight + GUTTER,
              width: fullWidth,
              height: fullWidthHeadingHeight
            }
          },
          textElement(
            textData,
            PADDING,
            PADDING + twoRowHeight + GUTTER + fullWidthHeadingHeight + GUTTER,
            fullWidth,
            twoRowHeight,
            true
          )
        ]
      })
    },
    // Layout 4 – With dynamic heading height
    {
      img: Layout4,
      layoutStyle: 'layout-4',
      previewImages: [
        { img: Layout4ImagePreview, type: 'IMAGE' },
        { img: Layout4VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => ({
        data: [
          ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
          mediaElement(
            processedMediaData,
            PADDING,
            PADDING,
            halfWidth,
            twoRowHeight,
            isVideo,
            0
          ),
          mediaElement(
            processedMediaData,
            PADDING + halfWidth + GUTTER,
            PADDING,
            halfWidth,
            twoRowHeight,
            isVideo,
            1
          ),
          {
            ...centeredHeading,
            params: {
              ...centeredHeading.params,
              x: PADDING,
              y: PADDING + twoRowHeight + GUTTER,
              width: fullWidth,
              height: fullWidthHeadingHeight
            }
          },
          textElement(
            textData,
            PADDING,
            PADDING + twoRowHeight + GUTTER + fullWidthHeadingHeight + GUTTER,
            fullWidth,
            twoRowHeight,
            true
          )
        ]
      })
    },
    // Layout 5 – With dynamic heading height for half-width column
    {
      img: Layout5,
      layoutStyle: 'layout-5',
      previewImages: [
        { img: Layout5ImagePreview, type: 'IMAGE' },
        { img: Layout5VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => {
        // Create a half-width, left-aligned heading specifically for this layout
        const halfWidthHeading = createElement('TEXT', 'HEADING', {
          content: leftAlignedHeading.params.content,
          x: PADDING,
          y: columnHeadingY,
          width: halfWidth,
          height: halfWidthHeadingHeight,
          media_format: 'TEXT'
        })

        return {
          data: [
            // Left column: heading and text
            halfWidthHeading,
            ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
            textElement(
              textData,
              PADDING,
              columnTextY,
              halfWidth,
              columnTextHeight
            ),
            // Right column: single media element (full column height)
            mediaElement(
              processedMediaData,
              PADDING + halfWidth + GUTTER,
              PADDING,
              halfWidth,
              containerContentHeight,
              isVideo,
              0
            )
          ]
        }
      }
    },
    // Layout 6 – With dynamic heading height for half-width column
    {
      img: Layout6,
      layoutStyle: 'layout-6',
      previewImages: [
        { img: Layout6ImagePreview, type: 'IMAGE' },
        { img: Layout6VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => {
        // Create a half-width, left-aligned heading specifically for this layout
        const halfWidthHeading = createElement('TEXT', 'HEADING', {
          content: leftAlignedHeading.params.content,
          x: PADDING,
          y: columnHeadingY,
          width: halfWidth,
          height: halfWidthHeadingHeight,
          media_format: 'TEXT'
        })

        return {
          data: [
            // Left column: heading and text
            halfWidthHeading,
            ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
            textElement(
              textData,
              PADDING,
              columnTextY,
              halfWidth,
              columnTextHeight
            ),
            // Right column: two media elements stacked vertically
            mediaElement(
              processedMediaData,
              PADDING + halfWidth + GUTTER,
              PADDING,
              halfWidth,
              mediaDoubleHeight,
              isVideo,
              0
            ),
            mediaElement(
              processedMediaData,
              PADDING + halfWidth + GUTTER,
              PADDING + mediaDoubleHeight + GUTTER,
              halfWidth,
              mediaDoubleHeight,
              isVideo,
              1
            )
          ]
        }
      }
    },
    // Layout 7 – With dynamic heading height for half-width column
    {
      img: Layout7,
      layoutStyle: 'layout-7',
      previewImages: [
        { img: Layout7ImagePreview, type: 'IMAGE' },
        { img: Layout7VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => {
        // Create a half-width, left-aligned heading specifically for this layout
        const halfWidthHeading = createElement('TEXT', 'HEADING', {
          content: leftAlignedHeading.params.content,
          x: PADDING + halfWidth + GUTTER,
          y: columnHeadingY,
          width: halfWidth,
          height: halfWidthHeadingHeight,
          media_format: 'TEXT'
        })

        return {
          data: [
            // Left column: single media element (full column height)
            mediaElement(
              processedMediaData,
              PADDING,
              PADDING,
              halfWidth,
              containerContentHeight,
              isVideo,
              0
            ),
            // Right column: heading and text
            halfWidthHeading,
            ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
            textElement(
              textData,
              PADDING + halfWidth + GUTTER,
              columnTextY,
              halfWidth,
              columnTextHeight
            )
          ]
        }
      }
    },
    // Layout 8 – With dynamic heading height for half-width column
    {
      img: Layout8,
      layoutStyle: 'layout-8',
      previewImages: [
        { img: Layout8ImagePreview, type: 'IMAGE' },
        { img: Layout8VideoPreview, type: 'VIDEO' }
      ],
      generateLayout: () => {
        // Create a half-width, left-aligned heading specifically for this layout
        const halfWidthHeading = createElement('TEXT', 'HEADING', {
          content: leftAlignedHeading.params.content,
          x: PADDING + halfWidth + GUTTER,
          y: columnHeadingY,
          width: halfWidth,
          height: halfWidthHeadingHeight,
          media_format: 'TEXT'
        })

        return {
          data: [
            // Left column: two media elements stacked vertically
            mediaElement(
              processedMediaData,
              PADDING,
              PADDING,
              halfWidth,
              mediaDoubleHeight,
              isVideo,
              0
            ),
            mediaElement(
              processedMediaData,
              PADDING,
              PADDING + mediaDoubleHeight + GUTTER,
              halfWidth,
              mediaDoubleHeight,
              isVideo,
              1
            ),
            // Right column: heading and text
            halfWidthHeading,
            ...(_.isEmpty(logoData) ? [] : [logoImageElement(logoData)]),
            textElement(
              textData,
              PADDING + halfWidth + GUTTER,
              columnTextY,
              halfWidth,
              columnTextHeight
            )
          ]
        }
      }
    },
    // Layout 9 (no media)
    {
      img: Layout9,
      layoutStyle: 'layout-9',
      previewImages: [{ img: Layout9ImagePreview, type: 'IMAGE' }],
      generateLayout: () => {
        return {
          data: [
            createElement('TEXT', 'CONTENT', {
              content:
                `<p class=\"ql-align-justify ql-font-mulish\" style=\"text-align: left; line-height: 1.3; font-size: 16px;\">` +
                textData +
                `</p>`,
              x: 132.99996948242188,
              y: 34,
              width: 664,
              height: 83,
              media_format: 'TEXT'
            }),
            createElement('IMAGE', 'IMAGE', {
              content: processedMediaData[0].content,
              content_id: processedMediaData[0].content_id,
              x: 130.99990844726562,
              y: 143,
              width: 672,
              height: 409,
              media_format: 'IMAGE'
            })
          ]
        }
      }
    }
  ]
}

export default createLayoutData
